import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { primary, navy, muted, surface, borderSoft } from '../Layout/Resources/Theme'

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(32, 16, 48, 0.45);
  backdrop-filter: blur(4px);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  opacity: ${props => (props.$open ? 1 : 0)};
  pointer-events: ${props => (props.$open ? 'auto' : 'none')};
  transition: opacity 0.22s ease;
  visibility: ${props => (props.$open || props.$mounted ? 'visible' : 'hidden')};
`

const Card = styled.div`
  width: ${props => props.$width || 'min(560px, 100%)'};
  max-width: 100%;
  max-height: min(92vh, 920px);
  background: ${surface};
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(16, 24, 40, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: translateY(${props => (props.$open ? '0' : '12px')}) scale(${props => (props.$open ? 1 : 0.98)});
  opacity: ${props => (props.$open ? 1 : 0)};
  transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease;
`

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 22px 24px 16px;
  border-bottom: 1px solid ${borderSoft};
  flex-shrink: 0;
`

const HeadText = styled.div`
  min-width: 0;
`

const Eyebrow = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${primary};
  margin-bottom: 4px;
`

const Title = styled.h2`
  margin: 0;
  font-size: 1.45rem;
  font-weight: 800;
  color: ${navy};
  line-height: 1.25;
  letter-spacing: -0.02em;
`

const Subtitle = styled.p`
  margin: 6px 0 0;
  font-size: 0.84rem;
  color: ${muted};
  line-height: 1.4;
`

const Close = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: none;
  background: #F3F4F6;
  color: #6B7280;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: #E5E7EB;
    color: ${navy};
  }
`

const Body = styled.div`
  padding: 20px 24px 24px;
  overflow-y: auto;
  flex: 1;
`

function parseTitleParts(title, eyebrow) {
  if (eyebrow) {
    return { eyebrow, heading: title }
  }
  if (typeof title !== 'string') {
    return { eyebrow: null, heading: title }
  }
  const match = title.match(/^(Registrar|Modificar|Editar|Nuevo|Nueva)\s+(.+)$/i)
  if (match) {
    return { eyebrow: match[1].toUpperCase(), heading: match[2] }
  }
  return { eyebrow: null, heading: title }
}

const SideDrawer = ({ open, onClose, title, eyebrow, subtitle, children, width }) => {
  const [mounted, setMounted] = useState(open)
  const parts = useMemo(() => parseTitleParts(title, eyebrow), [title, eyebrow])

  useEffect(() => {
    if (open) {
      setMounted(true)
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = prev
      }
    }

    const t = window.setTimeout(() => setMounted(false), 300)
    return () => window.clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (typeof document === 'undefined') return null
  if (!open && !mounted) return null

  return createPortal(
    <Overlay
      $open={open}
      $mounted={mounted}
      onClick={onClose}
      role="presentation"
    >
      <Card
        $open={open}
        $width={width}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        onClick={(e) => e.stopPropagation()}
      >
        <Head>
          <HeadText>
            {parts.eyebrow ? <Eyebrow>{parts.eyebrow}</Eyebrow> : null}
            <Title>{parts.heading}</Title>
            {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
          </HeadText>
          <Close type="button" onClick={onClose} aria-label="Cerrar">
            <i className="fa fa-times" />
          </Close>
        </Head>
        <Body className="sp-form-modal-body">{children}</Body>
      </Card>
    </Overlay>,
    document.body
  )
}

SideDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.node,
  eyebrow: PropTypes.node,
  subtitle: PropTypes.node,
  children: PropTypes.node,
  width: PropTypes.string
}

SideDrawer.defaultProps = {
  open: false,
  onClose: () => {},
  title: '',
  eyebrow: null,
  subtitle: null,
  width: null
}

export default SideDrawer
